import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  Paper,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Category as CategoryIcon,
  Repeat as RecurringIcon,
  Assessment as ReportsIcon,
  Notifications as NotificationsIcon,
  TipsAndUpdates as TipsIcon,
  PlayArrow as PlayIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { EXPENSE_HELP_CONTENT, CONTEXTUAL_HELP } from '@/utils/expense-help-content';

interface ExpenseHelpDialogProps {
  open: boolean;
  onClose: () => void;
  initialTab?: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`help-tabpanel-${index}`}
      aria-labelledby={`help-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const ExpenseHelpDialog: React.FC<ExpenseHelpDialogProps> = ({
  open,
  onClose,
  initialTab = 0,
}) => {
  const [tabValue, setTabValue] = useState(initialTab);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const renderHelpSection = (key: string, content: any) => (
    <Accordion key={key} sx={{ mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {content.title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {content.description}
        </Typography>
        
        {content.examples && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Examples:
            </Typography>
            <List dense>
              {content.examples.map((example: string, index: number) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 24 }}>
                    <PlayIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={example}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
        
        {content.tips && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Tips:
            </Typography>
            <List dense>
              {content.tips.map((tip: string, index: number) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 24 }}>
                    <TipsIcon fontSize="small" color="warning" />
                  </ListItemIcon>
                  <ListItemText
                    primary={tip}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );

  const renderGettingStarted = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Welcome to Expense Management
      </Typography>
      <Typography variant="body1" sx={{ mb: 3 }}>
        Track and manage all your property-related expenses efficiently. This guide will help you get started.
      </Typography>

      <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.50' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Quick Start Checklist
        </Typography>
        {CONTEXTUAL_HELP.firstTimeUser.steps.map((step, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
            <CheckIcon sx={{ fontSize: 20, color: 'primary.main', mr: 1, mt: 0.1 }} />
            <Typography variant="body2">{step}</Typography>
          </Box>
        ))}
      </Paper>

      {renderHelpSection('expenseManagement', EXPENSE_HELP_CONTENT.expenseManagement)}
      {renderHelpSection('oneTimeExpense', EXPENSE_HELP_CONTENT.oneTimeExpense)}
      {renderHelpSection('categories', EXPENSE_HELP_CONTENT.categories)}
    </Box>
  );

  const renderCategories = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Managing Expense Categories
      </Typography>
      <Typography variant="body1" sx={{ mb: 3 }}>
        Organize your expenses effectively using predefined and custom categories.
      </Typography>

      <Paper sx={{ p: 2, mb: 3, bgcolor: 'info.50' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Category Management Tips
        </Typography>
        {CONTEXTUAL_HELP.categoryManagement.steps.map((step, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
            <CategoryIcon sx={{ fontSize: 20, color: 'info.main', mr: 1, mt: 0.1 }} />
            <Typography variant="body2">{step}</Typography>
          </Box>
        ))}
      </Paper>

      {renderHelpSection('categories', EXPENSE_HELP_CONTENT.categories)}
      {renderHelpSection('customCategories', EXPENSE_HELP_CONTENT.customCategories)}
    </Box>
  );

  const renderRecurring = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Recurring Expenses
      </Typography>
      <Typography variant="body1" sx={{ mb: 3 }}>
        Automate tracking of regular expenses with fixed or variable amounts.
      </Typography>

      <Paper sx={{ p: 2, mb: 3, bgcolor: 'success.50' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Recurring Expense Setup
        </Typography>
        {CONTEXTUAL_HELP.recurringSetup.steps.map((step, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
            <RecurringIcon sx={{ fontSize: 20, color: 'success.main', mr: 1, mt: 0.1 }} />
            <Typography variant="body2">{step}</Typography>
          </Box>
        ))}
      </Paper>

      {renderHelpSection('fixedRecurring', EXPENSE_HELP_CONTENT.fixedRecurring)}
      {renderHelpSection('variableRecurring', EXPENSE_HELP_CONTENT.variableRecurring)}
      {renderHelpSection('frequency', EXPENSE_HELP_CONTENT.frequency)}
      {renderHelpSection('reminderTiming', EXPENSE_HELP_CONTENT.reminderTiming)}
    </Box>
  );

  const renderReports = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Reports and Analytics
      </Typography>
      <Typography variant="body1" sx={{ mb: 3 }}>
        Generate detailed reports to analyze spending patterns and prepare for taxes.
      </Typography>

      <Paper sx={{ p: 2, mb: 3, bgcolor: 'warning.50' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Report Generation Guide
        </Typography>
        {CONTEXTUAL_HELP.reportGeneration.steps.map((step, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
            <ReportsIcon sx={{ fontSize: 20, color: 'warning.main', mr: 1, mt: 0.1 }} />
            <Typography variant="body2">{step}</Typography>
          </Box>
        ))}
      </Paper>

      {renderHelpSection('expenseReports', EXPENSE_HELP_CONTENT.expenseReports)}
      {renderHelpSection('dateRange', EXPENSE_HELP_CONTENT.dateRange)}
      {renderHelpSection('exportOptions', EXPENSE_HELP_CONTENT.exportOptions)}
    </Box>
  );

  const renderBestPractices = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Best Practices
      </Typography>
      <Typography variant="body1" sx={{ mb: 3 }}>
        Follow these guidelines for effective expense management and accurate financial tracking.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Essential Best Practices
        </Typography>
        <List>
          {EXPENSE_HELP_CONTENT.bestPractices.tips?.map((tip, index) => (
            <ListItem key={index} sx={{ py: 0.5 }}>
              <ListItemIcon>
                <Chip
                  label={index + 1}
                  size="small"
                  color="primary"
                  sx={{ minWidth: 24, height: 24 }}
                />
              </ListItemIcon>
              <ListItemText
                primary={tip}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Common Scenarios
        </Typography>
        
        <Accordion sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Setting Up Your First Recurring Expense</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Start with a simple recurring expense like property insurance or a subscription service.
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="1. Choose 'Fixed Recurring' for consistent amounts" />
              </ListItem>
              <ListItem>
                <ListItemText primary="2. Set the correct frequency (usually monthly or yearly)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="3. Enter the exact amount from your bill" />
              </ListItem>
              <ListItem>
                <ListItemText primary="4. Set start date to your next billing date" />
              </ListItem>
              <ListItem>
                <ListItemText primary="5. Review the generated schedule before saving" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        <Accordion sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Handling Variable Utility Bills</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Utility bills vary by season and usage, making them perfect for variable recurring expenses.
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="1. Choose 'Variable Recurring' expense type" />
              </ListItem>
              <ListItem>
                <ListItemText primary="2. Set estimated range based on historical bills" />
              </ListItem>
              <ListItem>
                <ListItemText primary="3. Configure reminders 3-5 days before due date" />
              </ListItem>
              <ListItem>
                <ListItemText primary="4. Respond to reminders promptly with actual amounts" />
              </ListItem>
              <ListItem>
                <ListItemText primary="5. Review patterns quarterly to adjust estimates" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Preparing for Tax Season</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Organize your expenses throughout the year for easy tax preparation.
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="1. Categorize all expenses consistently" />
              </ListItem>
              <ListItem>
                <ListItemText primary="2. Keep digital receipts for all expenses" />
              </ListItem>
              <ListItem>
                <ListItemText primary="3. Generate annual reports by category" />
              </ListItem>
              <ListItem>
                <ListItemText primary="4. Export data in CSV format for accountants" />
              </ListItem>
              <ListItem>
                <ListItemText primary="5. Review property-specific expenses separately" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Expense Management Help</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab label="Getting Started" />
          <Tab label="Categories" />
          <Tab label="Recurring Expenses" />
          <Tab label="Reports" />
          <Tab label="Best Practices" />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 0, overflow: 'auto' }}>
        <Box sx={{ px: 3 }}>
          <TabPanel value={tabValue} index={0}>
            {renderGettingStarted()}
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            {renderCategories()}
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            {renderRecurring()}
          </TabPanel>
          <TabPanel value={tabValue} index={3}>
            {renderReports()}
          </TabPanel>
          <TabPanel value={tabValue} index={4}>
            {renderBestPractices()}
          </TabPanel>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Got it!
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExpenseHelpDialog;